import React, { useState } from "react";
import { useClientRelationships } from "../hooks/useClientRelationships";
import { REL_TYPES } from "../utils/relationshipTypes";

const S = {
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 2000, padding: "20px",
  },
  panel: {
    background: "white", width: "100%", maxWidth: "560px",
    borderRadius: "16px",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
    overflow: "hidden", display: "flex", flexDirection: "column", maxHeight: "85vh",
  },
  header: {
    background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
    padding: "16px 20px", color: "white",
    display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0,
  },
  closeBtn: {
    background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)",
    color: "white", width: "32px", height: "32px", borderRadius: "8px",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "18px",
  },
  body: { padding: "20px", overflowY: "auto", direction: "rtl", flex: 1 },
  input: {
    width: "100%", padding: "7px 10px", border: "1.5px solid #e2e8f0",
    borderRadius: 8, fontSize: 13, fontFamily: "inherit", direction: "rtl",
    outline: "none", boxSizing: "border-box", background: "white",
  },
  addBtn: {
    padding: "8px 18px", background: "linear-gradient(135deg, #1e3a5f, #2563eb)",
    color: "white", border: "none", borderRadius: 10, cursor: "pointer",
    fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
    boxShadow: "0 2px 6px rgba(37,99,235,0.3)",
  },
};

export default function RelationshipsModal({ clientId, clients, onSelectClient, onClose }) {
  const { relations, addRelation, removeRelation, toggleMyAccess, toggleTheirAccess } = useClientRelationships(clientId);
  const [newType, setNewType] = useState("spouse");
  const [newTarget, setNewTarget] = useState("");
  const [search, setSearch] = useState("");
  const [showResults, setShowResults] = useState(false);

  const clientName = clients.find(c => c.id === clientId)?.fileInfo?.client || "לקוח";
  const linkedIds = new Set(relations.map(r => r.targetId));
  const availableClients = clients.filter(c => c.id !== clientId && !linkedIds.has(c.id));

  const q = search.trim().toLowerCase();
  const filteredClients = q
    ? availableClients.filter(c =>
        (c.fileInfo?.client || "").toLowerCase().includes(q) ||
        (c.fileInfo?.clientId || "").toLowerCase().includes(q))
    : availableClients;

  const pickClient = (c) => {
    setNewTarget(c.id);
    setSearch(c.fileInfo?.client || "לקוח");
    setShowResults(false);
  };

  const handleAdd = () => {
    if (!newTarget) return;
    addRelation(newTarget, newType);
    setNewTarget("");
    setSearch("");
  };

  const handleNavigate = (targetId) => {
    onSelectClient(targetId);
    onClose();
  };

  return (
    <div style={S.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={S.panel}>
        {/* Header */}
        <div style={S.header}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontSize: "24px" }}>👪</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: "18px" }}>בני משפחה — {clientName}</div>
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                {relations.length} קשרים
              </div>
            </div>
          </div>
          <button onClick={onClose} style={S.closeBtn} title="סגור">✕</button>
        </div>

        {/* Body */}
        <div style={S.body}>
          {/* Add form */}
          <div style={{
            display: "flex", gap: 8, marginBottom: 18, padding: "14px",
            background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12,
            alignItems: "flex-end",
          }}>
            <div style={{ width: 130 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#1e40af", marginBottom: 4, display: "block" }}>
                סוג הקשר
              </label>
              <select value={newType} onChange={(e) => setNewType(e.target.value)} style={S.input}>
                {Object.entries(REL_TYPES).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, position: "relative" }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#1e40af", marginBottom: 4, display: "block" }}>
                לקוח
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setNewTarget(""); setShowResults(true); }}
                onFocus={() => setShowResults(true)}
                onBlur={() => setTimeout(() => setShowResults(false), 150)}
                placeholder={availableClients.length === 0 ? "אין לקוחות לקישור" : "🔍 חפש שם / ת.ז..."}
                style={S.input}
                disabled={availableClients.length === 0}
              />
              {showResults && availableClients.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", right: 0, left: 0, zIndex: 10,
                  marginTop: 4, background: "white", border: "1px solid #e2e8f0",
                  borderRadius: 8, boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
                  maxHeight: 200, overflowY: "auto",
                }}>
                  {filteredClients.length === 0 ? (
                    <div style={{ padding: "10px 12px", fontSize: 12, color: "#94a3b8" }}>
                      לא נמצאו לקוחות תואמים
                    </div>
                  ) : filteredClients.map(c => (
                    <div
                      key={c.id}
                      onMouseDown={() => pickClient(c)}
                      style={{
                        padding: "8px 12px", fontSize: 13, cursor: "pointer",
                        borderBottom: "1px solid #f1f5f9", textAlign: "right",
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                      onMouseLeave={e => e.currentTarget.style.background = "white"}
                    >
                      <span style={{ fontWeight: 700, color: "#1e293b" }}>{c.fileInfo?.client || "לקוח"}</span>
                      {c.fileInfo?.clientId && (
                        <span style={{ color: "#94a3b8", fontSize: 11, marginRight: 6 }}>{c.fileInfo.clientId}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={handleAdd} style={{ ...S.addBtn, opacity: newTarget ? 1 : 0.5 }} title="הוסף קשר">
              ➕ הוסף
            </button>
          </div>

          {availableClients.length === 0 && relations.length === 0 && (
            <div style={{ textAlign: "center", padding: "30px 20px", color: "#94a3b8", fontSize: 13 }}>
              אין לקוחות נוספים לקישור
            </div>
          )}

          {/* Relations list */}
          {relations.length === 0 ? (
            availableClients.length > 0 && (
              <div style={{ textAlign: "center", padding: "30px 20px", color: "#94a3b8", fontSize: 14 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>👨‍👩‍👧‍👦</div>
                <div>אין קשרי משפחה עדיין</div>
                <div style={{ fontSize: 12, marginTop: 4 }}>הוסף קשר ראשון למעלה</div>
              </div>
            )
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {relations.map(rel => {
                const target = clients.find(c => c.id === rel.targetId);
                const targetName = target?.fileInfo?.client || "לקוח לא ידוע";
                const label = REL_TYPES[rel.type]?.label || rel.type;
                return (
                  <div key={rel.id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 14px", border: "1px solid #e2e8f0", borderRadius: 10,
                    background: "white",
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: "#1e40af",
                      background: "#eff6ff", padding: "2px 8px", borderRadius: 6, whiteSpace: "nowrap",
                    }}>
                      {label}
                    </span>
                    <div
                      style={{
                        flex: 1, fontSize: 14, fontWeight: 700, color: target ? "#1e293b" : "#94a3b8",
                        cursor: target ? "pointer" : "default", textAlign: "right",
                      }}
                      onClick={() => target && handleNavigate(rel.targetId)}
                      title={target ? "מעבר לכרטיס הלקוח" : ""}
                    >
                      {targetName} {target && <span style={{ color: "#94a3b8", fontSize: 12 }}>›</span>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: 10, background: "#f8fafc", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0" }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 2 }}>גישה לפורטל משפחתי:</div>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155", cursor: "pointer" }} title="אפשר ללקוח הנוכחי לראות את התיק של בן המשפחה">
                        <input 
                          type="checkbox" 
                          checked={!!rel.portalAccess} 
                          onChange={(e) => toggleMyAccess(rel.id, e.target.checked)} 
                          style={{ cursor: "pointer" }}
                        />
                        <span><strong style={{color:"#0f172a"}}>{clientName.split(' ')[0]}</strong> יכול/ה לראות את התיק של <strong style={{color:"#0f172a"}}>{targetName.split(' ')[0]}</strong></span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#334155", cursor: "pointer" }} title="אפשר לבן המשפחה לראות את התיק של הלקוח הנוכחי">
                        <input 
                          type="checkbox" 
                          checked={!!rel.theirPortalAccess} 
                          onChange={(e) => toggleTheirAccess(rel.id, e.target.checked)} 
                          style={{ cursor: "pointer" }}
                        />
                        <span><strong style={{color:"#0f172a"}}>{targetName.split(' ')[0]}</strong> יכול/ה לראות את התיק של <strong style={{color:"#0f172a"}}>{clientName.split(' ')[0]}</strong></span>
                      </label>
                    </div>
                    <button
                      onClick={() => removeRelation(rel.id)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "#ef4444", fontSize: 14, padding: "4px 6px",
                      }}
                      title="הסר קשר"
                    >
                      🗑️
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
